/**
 * Create Service from Simulation Modal Component
 * 
 * Modal for creating a new service setting from the current simulation configuration.
 * Allows users to save their simulation settings as a reusable service setting.
 */

import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Box,
  Alert,
} from '@mui/material';

import type { Service, ProcessingTime } from '../../../types/service';
import { createEmptyService } from '../../../types/service';
import { AppContext } from '../../../context/context';
import { logEvent } from '../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
interface SimulationConfig {
  scenarioName: string;
  timeWindowSlope: number;
  timeWindowIntercept: number;
  timeWindowFixedWindow: number;
  maxTripTimeMinutes?: number;
  travelTimeMultiplier: number;
  useVariableMultipliers: boolean;
  hourlyMultipliers?: number[];
  processingTimes?: Array<{ seatType: string; processingTimeSeconds: number }>;
  onceOffProcessingTime?: number;
  passengerTypes?: Array<{ passenger_type: string; processing_time: number; probability: number }>;
  numberOfPassengers?: Array<{ number_of_passengers: number; probability: number }>;
}

interface CreateServiceFromSimulationModalProps {
  open: boolean;
  onClose: () => void;
  simulationConfig: SimulationConfig | null;
  onServiceCreated?: (serviceId: string) => void;
}

const CreateServiceFromSimulationModal: React.FC<CreateServiceFromSimulationModalProps> = ({
  open,
  onClose,
  simulationConfig,
  onServiceCreated,
}) => {
  const context = useContext(AppContext);
  const [serviceName, setServiceName] = useState('');
  const [description, setDescription] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Reset form when modal opens/closes or config changes
  React.useEffect(() => {
    if (open && simulationConfig) {
      setServiceName(simulationConfig.scenarioName || 'New Service');
      setDescription('');
      setValidationErrors([]);
    }
  }, [open, simulationConfig]);

  const handleSave = () => {
    if (!simulationConfig || !context?.global?.setServices) {
      setValidationErrors(['Service context not available']);
      return;
    }

    const errors: string[] = [];
    
    if (!serviceName.trim()) {
      errors.push('Service name is required');
    }
    
    if (!simulationConfig.timeWindowSlope || simulationConfig.timeWindowSlope <= 0) {
      errors.push('Time window slope must be positive');
    }
    
    if (simulationConfig.timeWindowIntercept < 0) {
      errors.push('Time window intercept must be non-negative');
    }
    
    if (simulationConfig.timeWindowFixedWindow < 0) {
      errors.push('Fixed window must be non-negative');
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Map simulation config to service
    const processingTimes: ProcessingTime[] = [];
    
    // Use processingTimes if available, otherwise derive from passengerTypes
    if (simulationConfig.processingTimes && simulationConfig.processingTimes.length > 0) {
      processingTimes.push(...simulationConfig.processingTimes.map(pt => ({
        seatType: pt.seatType,
        processingTimeSeconds: pt.processingTimeSeconds,
      })));
    } else if (simulationConfig.passengerTypes && simulationConfig.passengerTypes.length > 0) {
      processingTimes.push(...simulationConfig.passengerTypes.map(pt => ({
        seatType: pt.passenger_type,
        processingTimeSeconds: pt.processing_time,
      })));
    } else {
      // Default processing times
      processingTimes.push(
        { seatType: 'standard', processingTimeSeconds: 30 },
        { seatType: 'wheelchair', processingTimeSeconds: 90 }
      );
    }

    // Derive maxCompanions from numberOfPassengers
    const maxCompanions = simulationConfig.numberOfPassengers && simulationConfig.numberOfPassengers.length > 0
      ? Math.max(...simulationConfig.numberOfPassengers.map(p => p.number_of_passengers))
      : 6;

    const newService: Service = {
      ...createEmptyService(serviceName.trim()),
      description: description.trim() || undefined,
      timeWindowSettings: {
        slope: simulationConfig.timeWindowSlope,
        intercept: simulationConfig.timeWindowIntercept,
        fixedWindow: simulationConfig.timeWindowFixedWindow,
      },
      maxTripTimeMinutes: simulationConfig.maxTripTimeMinutes || 60,
      travelTimeMultiplier: simulationConfig.travelTimeMultiplier,
      useVariableMultipliers: simulationConfig.useVariableMultipliers,
      hourlyMultipliers: simulationConfig.useVariableMultipliers && simulationConfig.hourlyMultipliers
        ? [...simulationConfig.hourlyMultipliers]
        : undefined,
      processingTimes,
      onceOffProcessingTime: simulationConfig.onceOffProcessingTime || processingTimes[0]?.processingTimeSeconds || 15,
      maxCompanions,
    };

    // Add service to context
    context.global.setServices((prev: Service[]) => [...prev, newService]);

    logEvent({
      category: 'Service Management',
      action: 'ServiceCreatedFromSimulation',
      label: newService.name,
      service_id: newService.id
    });

    // Call callback if provided
    if (onServiceCreated) {
      onServiceCreated(newService.id);
    }

    onClose();
  };

  const handleClose = () => {
    setValidationErrors([]);
    onClose();
  };

  if (!simulationConfig) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={20} name="gear" />
            <Typography variant="h6">
              Create Service from Simulation Settings
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Icon size={20} name="xmark" />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={2}>
          {validationErrors.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>Validation Errors:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            Save your current simulation settings as a reusable service setting. This will create a new service that you can select in future simulations.
          </Typography>

          <TextField
            label="Service Name"
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            required
            fullWidth
            helperText="Name for this service setting"
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            helperText="Optional description for this service setting"
          />

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Settings to be saved:
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                <strong>Time Window:</strong> Slope {simulationConfig.timeWindowSlope}, Intercept {simulationConfig.timeWindowIntercept}s, Fixed {simulationConfig.timeWindowFixedWindow}s
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Travel Time Multiplier:</strong> {simulationConfig.travelTimeMultiplier}x
                {simulationConfig.useVariableMultipliers && ' (with hourly multipliers)'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Processing Times:</strong> {simulationConfig.processingTimes?.length || simulationConfig.passengerTypes?.length || 0} configured
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Create Service
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateServiceFromSimulationModal;

